import React, { useEffect, useState } from "react";
import { BasePageModal } from "@/shared/components/modal/page-modal/base-page-modal";
import { MailAddressForm } from "@/shared/components/text-box/mail-address-form";
import { useShareAccountGroup } from "@/features/my-data/hooks/use-share-account-group";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { Checkbox } from "@/shared/components/check-box/check-box";
import { useInputValidation } from "@/shared/hooks/use-input-validation";


interface Props {
  isOwner: number;
  onTapCloseModal: () => void;
  onTapCompleteModal: () => void;
  onTapOpenCancelConfirm: (memberId: string, cancelType: "share" | "invite", targetMail: string) => void;
}


export const ShareMemberModal = ({
  isOwner,
  onTapCloseModal,
  onTapCompleteModal,
  onTapOpenCancelConfirm,
}: Props) => {
  const {
    targetAccountGroup,
  } = useAccountGroupsContext();
  const {
    ownerMembers,
    setOwnerMembers,
    shareMembers,
    setShareMembers,
    invitedMembers,
    setInvitedMembers,
    check,
    setCheck,
    invitationMail,
    setInvitationMail,
    invitationComment,
    setInvitationComment,
    isActiveInviteButton,
    getShareMembers,
    updateShareMembers,
    onClickSendMail,
    changeTerminateButtonState,
    changeSendMailButtonState
  } = useShareAccountGroup({onTapCompleteModal});
  const [refreshSharedMember, setRefreshSharedMember] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // 共有メンバーの取得
  useEffect(() => {
    const fetchShareMembers = async () => {
      const members = await getShareMembers(targetAccountGroup.accountGroupId);
      if (members) {
        setOwnerMembers(members.filter(member => member.status === -1));
        setShareMembers(members.filter(member => member.status === 1));
        setInvitedMembers(members.filter(member => member.status === 0));
      }
      setIsLoading(false);
    };
    fetchShareMembers();
  }, [targetAccountGroup, refreshSharedMember]);

  useEffect(() => {
    changeTerminateButtonState();
  }, [check]);

  useEffect(() => {
    changeSendMailButtonState();
  }, [invitationMail]);

  const { validate } = useInputValidation("deleteOver4Bytes");
  const [isComposing, setIsComposing] = useState(false);

  const onChangeComment = (value: string, composing = isComposing) => {
    value = validate(value, composing);
    setInvitationComment(value);
  }

  return (
    <BasePageModal
      title="データ共有"
      handleClose={() => {
        onTapCloseModal();
      }}
      className="md-group-shear-status"
      bottomChildren={
        (isOwner === 0 ? (
          <BaseRoundButton
            buttonText="共有を解除する"
            className="btn-cancel-share"
            id="share-member-modal-001"
            loadingNegative={true}
            onClick={async () => {
              await updateShareMembers(
                targetAccountGroup.accountGroupId,
                ownerMembers[0].id,
                3  // 解除
              );
              onTapCloseModal();
            }}
            isActive={check}
          />
        ):(
          <BaseRoundButton
            buttonText="招待メールを送信"
            className="btn-mail"
            id="share-member-modal-002"
            loadingNegative={true}
            onClick={() => {
              onClickSendMail(
                targetAccountGroup.accountGroupId,
                invitationMail,
                invitationComment.trim()
              );
            }}
            isActive={isActiveInviteButton}
          />
        ))
      }
      disableOverlayClose={true}
    >
      <h2>
        共有ステータス
      </h2>
      {isOwner === 0 ? (
        <>
        <dl className="status">
          <dt>共有者</dt>
          <dd>
            <div className="share-member-item">
              <span>
                {ownerMembers[0]?.mail}
              </span>
            </div>
          </dd>
        </dl>
        <Checkbox
          id="terminateShare"
          label="共有を解除する場合はこちらにチェックを入れて「共有を解除する」ボタンを押してください"
          checked={check}
          handleChange={(check) => {
            setCheck(check);
          }}
        />
        </>
      ) : (
        <>
        <dl className="status">
          <dt>共有中</dt>
          <dd>
          {shareMembers.map((member, index) => (
            <div key={index} className="share-member-item">
              <span>
                {member.mail}
                <BaseRoundButton
                  title="共有を取り消し"
                  onClick={() => {
                    onTapOpenCancelConfirm(member.id, "share", member.mail);
                  }}
                />
              </span>
            </div>
          ))}
          </dd>
          <dt>招待中</dt>
          <dd>
          {invitedMembers.map((member, index) => (
            <div key={index} className="share-member-item">
              <span>{member.mail}
                <BaseRoundButton
                  title="招待を取り消し"
                  onClick={() => {
                    onTapOpenCancelConfirm(member.id, "invite", member.mail);
                  }}
                />
              </span>
            </div>
          ))}
          </dd>
        </dl>
        <h3>
          招待メールの送信
        </h3>
        <div className="group-name md-bank-style share-member-style">
          <div className="label">グループ名</div>
          <div className="value">{targetAccountGroup.accountGroupsName}</div>
        </div>
        <ul className="invitation">
          <li>
            <MailAddressForm
              title="招待する相手"
              placeholder="メールアドレスを入力"
              id="invitationMail"
              onChange={(value) => {setInvitationMail(value);}}
            />
          </li>
          <li>
            <label htmlFor="invitationComment">メッセージ（任意）</label>
            <textarea
              id="invitationComment"
              value={invitationComment}
              onChange={(e) => {onChangeComment(e.target.value);}}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={() => {
                setIsComposing(false);
                onChangeComment(invitationComment, false);
              }}
              maxLength={2000}
            />
          </li>
        </ul>
        <p>
          送信相手が招待を受け入れるとデータ共有を開始します
        </p>
        </>
      )}
    </BasePageModal>
  );
};
